import React, { useState, useEffect, ReactNode, MouseEvent, useRef } from 'react';
import moment, { Moment } from 'moment';

import { NewDatePickerProps } from '../../../NewDatePicker/NewDatePicker';

import Icon from '../../../Icon/Icon';

import { SHORT_DATE, FULL_DATE } from './Constants';
import {
  getLocalizedMonths,
  getMomentDate,
  defineInitialSuggestedMonth,
  defineInitialSuggestedYear,
  getLocalizedMonth,
} from '../helpers';
import { useCalendarKeyboardNavigation } from './useCalendarKeyboardNavigation';
import { LOCALES } from '../../../../helpers/datetime';

import {
  CalendarArrow,
  CalendarPanel,
  CalendarPanelSection,
  CalendarControl,
  CalendarControlFake,
  CalendarControlInput,
  CalendarControlSelect,
  CalendarControlSelectArrow,
  CalendarTable,
} from './styles';
import CalendarDays from './CalendarDays/CalendarDays';
import CalendarHeader from './CalendarHeader/CalendarHeader';
import { CustomDate, CalendarShortcutsProps } from './CalendarShortcuts/CalendarShortcuts';

export type DateMask = 'MM/DD/YYYY' | 'MM/YYYY';

export interface CalendarProps {
  value: string;
  onChange: (e: MouseEvent<HTMLButtonElement>, value: string) => void;
  closeCalendar: () => void;
  dateMask: DateMask;
  format?: NewDatePickerProps['format'];
  locale?: NewDatePickerProps['locale'];
  selectedDate: CustomDate;
  setSelectedDate: (arg1) => void;
  onDateClick: (e: any, day: Moment) => void;
  /** For a given date passed in as an argument, return `false` to disable it. */
  disableDates?: (date: string) => boolean;
  /** RenderProp for Shortcuts. Should return a NewDatePicker.Shortcuts.
   * You can spread the props from the callback into the Component. */
  shortcuts?: (shortcutsProps: CalendarShortcutsProps) => ReactNode;
  /** Marks the element with a given ID to make it easier to select in tests */
  'data-testid'?: string | number;
  /** Allows screen readers to describe the content */
  'aria-label'?: string;
}

/**
 * @purpose Allows users to choose dates from a calendar
 * @group NewDatePicker
 */
function Calendar(props: CalendarProps) {
  const {
    value,
    dateMask,
    format,
    locale = LOCALES.EN_US,
    disableDates = () => false,
    onChange,
    closeCalendar,
    shortcuts,
    selectedDate,
    setSelectedDate,
    onDateClick,
    'data-testid': dataTestId,
    'aria-label': ariaLabel,
    ...otherProps
  } = props;

  const [suggestedMonth, setSuggestedMonth] = useState(defineInitialSuggestedMonth(value, dateMask));
  const [suggestedYear, setSuggestedYear] = useState(defineInitialSuggestedYear(value, dateMask));

  // eslint-disable-next-line ssr-friendly/no-dom-globals-in-react-fc
  const calendarRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // we don't need to validate input when using format nor need to setSelectedDate
    // this is all done by dateTimeUtils and CoreDatePicker's handleChange
    if (format && format === 'condensed') {
      setSuggestedMonth(undefined);
      setSuggestedYear(undefined);
    }

    const dateFormat = new RegExp(/^(([01]\d)[.\-/\\ ])?([0123]\d)[.\-/\\ ]((19|20)\d{2})/g);

    // this condition will enforce the selected date to only be valid when:
    // 1. the date is valid
    // 2. the date format is valid
    // 3. the user stops typing (when value.length === dateMask.length)
    if (value.length >= dateMask.length && dateFormat.test(value) && moment(value, dateMask).isValid()) {
      setSelectedDate(value);
      if (dateMask === FULL_DATE) {
        setSuggestedMonth(undefined);
        setSuggestedYear(undefined);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, dateMask]);

  const getCalendarDisplayDate = () => {
    const month = getMomentDate(selectedDate, dateMask)
      ? getMomentDate(selectedDate, dateMask).get('month')
      : moment().get('month');
    const year = getMomentDate(selectedDate, dateMask).get('year') || moment().get('year');

    if (dateMask === SHORT_DATE) {
      return moment([year, month]);
    }
    return moment([suggestedYear || year, suggestedMonth || month]);
  };

  const handleMonthChange = (e, month, setStateCallback = () => {}) => {
    const targetMonth = getMomentDate(selectedDate, dateMask).month(month);

    if (dateMask === SHORT_DATE) {
      onChange(e, targetMonth.format(dateMask));
      setSelectedDate(targetMonth);
      setSuggestedMonth(month);
      setStateCallback();
    } else {
      setSuggestedMonth(month);
      setStateCallback();
    }
  };

  const handleYearChange = (e, year) => {
    const targetYear = getMomentDate(selectedDate, dateMask).year(year);

    if (dateMask === SHORT_DATE) {
      onChange(e, targetYear.format(dateMask));
      setSelectedDate(targetYear);
      setSuggestedYear(year);
    } else {
      setSuggestedYear(year);
    }
  };

  const handleYearStep = (e, step) => {
    const newYear = getCalendarDisplayDate().get('year') + step;
    handleYearChange(e, newYear.toString());
  };

  const handleMonthStep = (e, step) => {
    const newMonth = getCalendarDisplayDate().get('month') + step;

    if (newMonth > 11) {
      handleMonthChange(e, '0', () => handleYearStep(e, 1));
    } else if (newMonth < 0) {
      handleMonthChange(e, '11', () => handleYearStep(e, -1));
    } else {
      handleMonthChange(e, newMonth.toString());
    }
  };

  useCalendarKeyboardNavigation({
    calendarRef,
    handleMonthStep,
    handleYearStep,
  });

  return (
    <div {...otherProps} data-testid={dataTestId} aria-label={ariaLabel} ref={calendarRef}>
      <CalendarPanel>
        <CalendarPanelSection flex={3}>
          <CalendarArrow type="button" onClick={e => handleMonthStep(e, -1)}>
            <Icon type="backward-bold" meaning="previous month" />
          </CalendarArrow>
          <CalendarControl>
            <CalendarControlFake
              value={getLocalizedMonth(
                getCalendarDisplayDate().get('month'),
                typeof locale === 'object' ? locale.output : locale,
              )}
              tabIndex={-1}
              readOnly
              aria-label={ariaLabel ? `${ariaLabel} (read only)` : 'calendar input (read only)'}
            />
            <CalendarControlSelect
              value={getCalendarDisplayDate().get('month')}
              onChange={e => handleMonthChange(e, e.target.value)}
              aria-label={ariaLabel ? `${ariaLabel} select` : 'calendar select'}
              data-field-type="month"
            >
              {getLocalizedMonths(typeof locale === 'object' ? locale.output : locale).map((month, idx) => (
                <option aria-selected={moment(value, dateMask).get('month') === idx} key={idx} value={idx}>
                  {month}
                </option>
              ))}
            </CalendarControlSelect>
            <CalendarControlSelectArrow>
              <Icon type="down" size="xs" meaning="choose month" />
            </CalendarControlSelectArrow>
          </CalendarControl>
          <CalendarArrow type="button" onClick={e => handleMonthStep(e, 1)}>
            <Icon type="forward-bold" meaning="next month" />
          </CalendarArrow>
        </CalendarPanelSection>
        <CalendarPanelSection marginLeft="10px" flex={2}>
          <CalendarArrow type="button" onClick={e => handleYearStep(e, -1)}>
            <Icon type="backward-bold" meaning="previous year" />
          </CalendarArrow>
          <CalendarControl>
            <CalendarControlInput
              type="text"
              maxLength={4}
              value={getCalendarDisplayDate().get('year')}
              onChange={e => handleYearChange(e, e.target.value)}
              aria-label={ariaLabel ? `${ariaLabel} input` : 'calendar input'}
              data-field-type="year"
            />
          </CalendarControl>
          <CalendarArrow type="button" onClick={e => handleYearStep(e, 1)}>
            <Icon type="forward-bold" meaning="next year" />
          </CalendarArrow>
        </CalendarPanelSection>
      </CalendarPanel>
      {Boolean(dateMask === FULL_DATE) && (
        <CalendarTable data-testid={`${dataTestId}-table`}>
          <thead>
            <CalendarHeader selectedDate={selectedDate} dateMask={dateMask} />
          </thead>
          <tbody>
            <CalendarDays
              format={format}
              value={value}
              selectedDate={selectedDate}
              dateMask={dateMask}
              disableDates={disableDates}
              onDateClick={onDateClick}
              monthStart={getCalendarDisplayDate().startOf('month')}
              monthEnd={getCalendarDisplayDate().endOf('month')}
            />
          </tbody>
        </CalendarTable>
      )}
    </div>
  );
}

Calendar.defaultProps = {
  disableDates: undefined,
  shortcuts: undefined,
};

Calendar.displayName = 'Calendar';

export default Calendar;
